import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  booleanAttribute,
  effect,
  inject,
  input,
  OnInit,
  output,
  viewChild,
  AfterViewInit,
} from '@angular/core';
import {
  FormControl,
  FormControlDirective,
  FormControlName,
  NgModel,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormField,
  MatFormFieldModule,
  SubscriptSizing,
} from '@angular/material/form-field';
import {
  DateRange as DatePickerDateRange,
  ExtractDateTypeFromSelection,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatDatepickerInputEvent,
  MatDatepickerModule,
  MatDatepickerToggle,
  MatDateRangeInput,
  MatDateRangePicker,
} from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import {
  DateAdapter,
  ErrorStateMatcher,
  MAT_DATE_FORMATS,
  MatNativeDateModule,
} from '@angular/material/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FieldErrorStateMatcher, injectNgControl } from '@rp/utils';
import { NoopValueAccessorDirective } from '@rp/shared/directives';
import { TranslateModule } from '@ngx-translate/core';
import { DateRange } from '@rp/shared/models';
import { IncludesPipe, ObjectKeysToArrayPipe } from '@rp/shared/pipes';
import { Validation } from '@rp/shared/validators';

import { IconComponent, IconName } from '../icon';
import { DatePickerHeaderComponent } from './date-picker-header';
import { DatePickerActionsComponent } from './date-picker-actions/date-picker-actions.component';
import { CustomDateAdapter } from './adapters/custom-date-picker.adapter';
import { LivePreviewStrategy } from './live-preview-strategy';

@Component({
  selector: 'rp-date-picker',
  standalone: true,
  templateUrl: './date-picker.component.html',
  hostDirectives: [NoopValueAccessorDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatFormFieldModule,
    MatDatepickerModule,
    MatInputModule,
    ReactiveFormsModule,
    IconComponent,
    TranslateModule,
    IncludesPipe,
    ObjectKeysToArrayPipe,
    DatePickerActionsComponent,
    MatNativeDateModule,
  ],
  providers: [
    LivePreviewStrategy,
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { floatLabel: 'always' } },
    { provide: ErrorStateMatcher, useClass: FieldErrorStateMatcher },
    { provide: MAT_DATE_RANGE_SELECTION_STRATEGY, useExisting: LivePreviewStrategy },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: ['dd.MM.yyyy'],
        },
        display: {
          dateInput: 'dd.MM.yyyy',
          monthYearLabel: 'MMM yyyy',
          dateA11yLabel: 'dd.MM.yyyy',
          monthYearA11yLabel: 'MMM yyyy',
        },
      },
    },
  ],
})
export class DatePickerComponent implements OnInit, AfterViewInit {
  dateRangeInput = viewChild(MatDateRangeInput);
  picker = viewChild('picker', { read: MatDateRangePicker<DateRange> });
  formField = viewChild('formField', { read: MatFormField });
  datePickerToggle = viewChild('datePickerToggle', { read: MatDatepickerToggle });

  maxDate = input<Date | null>(new Date());
  minDate = input<Date | null>(null);
  label = input<string>('');
  hint = input<string>('');
  placeholder = input<string>('');
  placeholderStart = input<string>('');
  placeholderEnd = input<string>('');
  startDateKey = input<string>('startDate');
  endDateKey = input<string>('endDate');
  subscriptSizing = input<SubscriptSizing>('fixed');
  matcher = input<FieldErrorStateMatcher>();
  isDisabled = input(false, {
    transform: booleanAttribute,
  });
  isRequired = input(false, {
    transform: booleanAttribute,
  });
  showErrorMessage = input(true, {
    transform: booleanAttribute,
  });
  clearable = input(false, {
    transform: booleanAttribute,
  });

  dateChange = output<MatDatepickerInputEvent<ExtractDateTypeFromSelection<DateRange>>>();

  hasRange = false;

  ngControl: FormControlDirective | FormControlName | NgModel = injectNgControl();
  startControl = new FormControl<Date | null>(null);
  endControl = new FormControl<Date | null>(null);
  datePickerHeader = DatePickerHeaderComponent;
  icons = IconName;

  readonly patternValidations: Validation[] = [
    Validation.DatePickerParse,
    Validation.DatePickerMax,
  ];

  private readonly _destroyRef = inject(DestroyRef);
  private readonly _timeOut: ReturnType<typeof setTimeout>;
  private readonly _livePreviewStrategy = inject(LivePreviewStrategy);

  constructor() {
    effect(() => {
      const { control } = this.ngControl;

      if (this.isDisabled()) {
        control.disable({ emitEvent: false });
        this.startControl.disable({ emitEvent: false });
        this.endControl.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
        this.startControl.enable({ emitEvent: false });
        this.endControl.enable({ emitEvent: false });
      }

      if (this.isRequired() || this.ngControl.control.errors?.['required']) {
        control.addValidators(Validators.required);
        this.startControl.addValidators(Validators.required);
        this.endControl.addValidators(Validators.required);
      } else {
        control.removeValidators(Validators.required);
        this.startControl.removeValidators(Validators.required);
        this.endControl.removeValidators(Validators.required);
      }

      if (this.placeholderStart() || this.placeholderEnd()) {
        // Trigger change detection to update the view when the placeholder changes
        this._updateDateRangeInputView();
      }

      this._handleDateChange(this._livePreviewStrategy.selectedDate());
    });
  }

  ngOnInit(): void {
    this._setRangeControlsValues();

    this.ngControl.valueChanges?.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(e => {
      if (e === null) {
        this._livePreviewStrategy.reset();
        this.startControl.setValue(null, { emitEvent: false });
        this.endControl.setValue(null, { emitEvent: false });
      } else {
        if (this.startControl.value !== e?.[this.startDateKey()]) {
          this.startControl.setValue(e?.[this.startDateKey()], { emitEvent: false });
        }

        if (this.endControl.value !== e?.[this.endDateKey()]) {
          this.endControl.setValue(e?.[this.endDateKey()], { emitEvent: false });
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.picker()
      ?.openedStream.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        // (any) because - Angular material picker has private overlayRef
        const overlayRef = (this.picker() as any)._overlayRef;
        if (overlayRef) {
          const positionStrategy = overlayRef.getConfig().positionStrategy;
          positionStrategy
            .withPositions([
              {
                originX: 'start',
                originY: 'bottom',
                overlayX: 'start',
                overlayY: 'top',
              },
            ])
            .withPush(false);
          overlayRef.updatePosition();
        }

        if (this.datePickerToggle()) {
          requestAnimationFrame(() => {
            const { bottom: formFieldBottomPosition } =
              this.formField()._elementRef.nativeElement.getBoundingClientRect();
            const spaceBetweenInputAndViewPortBottomEdge =
              window.innerHeight - formFieldBottomPosition;

            document.documentElement.style.setProperty(
              '--datepicker-max-height',
              `${spaceBetweenInputAndViewPortBottomEdge}px`,
            );
          });
        }
      });
  }

  onClose(): void {
    clearTimeout(this._timeOut);
  }

  onClear(): void {
    this._livePreviewStrategy.reset();
    this.ngControl.control.reset();
    this.ngControl.control.markAsDirty();
    this.dateChange.emit(null);
  }

  onSelectPreset(selectedDate: DateRange): void {
    this.ngControl.control.setValue({
      [this.startDateKey()]: selectedDate.startDate,
      [this.endDateKey()]: selectedDate.endDate,
    });
    this.ngControl.control.markAsDirty();
    this.ngControl.control.markAsTouched();
    this.ngControl.control.setErrors(this.startControl.errors);
    this.dateChange.emit(this.ngControl.value);
    this.picker().close();
    this._updateDateRangeInputView();
  }

  onStartDateChange(date: Date): void {
    this.ngControl.control.setValue({
      ...this.ngControl.control.value,
      [this.startDateKey()]: date,
      ...(this.hasRange && {
        [this.endDateKey()]: null,
      }),
    });
    this.ngControl.control.setErrors(this.startControl.errors);
    this.ngControl.control.markAsDirty();

    this.dateChange.emit(this.ngControl.value);

    this._updateDateRangeInputView();
  }

  onEndDateChange(date: Date): void {
    const currentFormValue = this.ngControl.control.value;
    const selectedEndDate = currentFormValue[this.endDateKey()];

    if (this.hasRange && !selectedEndDate && !date) return;

    this.ngControl.control.setValue({
      ...currentFormValue,
      [this.endDateKey()]: date,
    });

    this.ngControl.control.setErrors(this.endControl.errors);
    this.ngControl.control.markAsDirty();

    this.dateChange.emit(this.ngControl.value);
    this.picker().close();
    this._updateDateRangeInputView();
  }

  private _handleDateChange({ start, end }: DatePickerDateRange<Date>): void {
    if (!start && !end) {
      return;
    } else if (end) {
      this.onEndDateChange(end);
    } else {
      this.onStartDateChange(start);
    }
  }

  private _setRangeControlsValues(): void {
    this.hasRange =
      !!this.ngControl.control.value && !!Object.keys(this.ngControl.control.value).length;

    if (this.hasRange && this.startDateKey() && this.endDateKey()) {
      this.startControl.setValue(this.ngControl.value?.[this.startDateKey()]);
      this.endControl.setValue(this.ngControl.value?.[this.endDateKey()]);
    }
  }

  private _updateDateRangeInputView(): void {
    if (this.dateRangeInput()) {
      this.dateRangeInput()['_changeDetectorRef'].markForCheck();
    }
  }
}
